import { purchaseCareItemSequence } from 'src/game/care/care.sequence';
import { getPet } from 'src/game/pet/index';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { puzzleCoinsConfirmCareItem } from 'src/game/puzzlecoins/puzzlecoins.sequence';
import type { CareItemID, CareItemType } from 'src/replicant/care/care.ruleset';
import {
  getCurrentPetState,
  hasPermanentStatusEffect,
} from 'src/replicant/fate/fate.getters';
import type { State } from 'src/replicant/State';
import { dreamDriftGetCurrentEvent } from 'src/replicant/dreamDrift/dreamDrift.getters';
import { assertNever } from 'src/replicant/utils';
import { now, user } from 'src/StateObserver';

/** Outcome returned after checking and resolving Dream Drift pet gates. */
export type DreamDriftPetHandlingResult = 'notNeeded' | 'handled' | 'blocked';

/** Care action and intro presentation required by the current pet state. */
export type DreamDriftCareGateResult =
  | {
      action: 'revive';
    }
  | {
      action: 'blocked';
      reason: 'asleep' | 'wantToGoOut' | 'daycare';
    }
  | {
      action: 'care';
      careType: CareItemType;
      itemFilter: CareItemID;
    };

/**
 * Resolve the Dream Drift care action required by the current pet state.
 *
 * @param state Player state to inspect.
 * @param currentTime Server-authoritative timestamp used to derive pet state.
 * @returns The required care gate, or null when the pet can enter normally.
 */
export function dreamDriftGetCareGate(
  state: State,
  currentTime: number,
): DreamDriftCareGateResult | null {
  // Resolve the visible pet state without considering hunger or love meters.
  let petState = getCurrentPetState(state, currentTime);

  // Dead, sleeping, and waiting pets retain priority over the daycare status.
  const hasHigherPriorityState =
    petState === 'dead' || petState === 'asleep' || petState === 'wantToGoOut';
  const isInDaycare = hasPermanentStatusEffect(state, 'daycare', currentTime);
  if (!hasHigherPriorityState && isInDaycare) petState = 'daycare';

  // Select the Dream Drift gate for every possible pet state.
  switch (petState) {
    case 'dead':
      return {
        action: 'revive',
      };

    case 'asleep':
    case 'wantToGoOut':
    case 'daycare':
      return {
        action: 'blocked',
        reason: petState,
      };

    case 'dying':
      return {
        action: 'care',
        careType: 'first_aid',
        itemFilter: 'fa_superPerfume',
      };

    case 'sick':
      return {
        action: 'care',
        careType: 'first_aid',
        itemFilter: 'fa_superCapsule',
      };

    case 'toothDecay':
      return {
        action: 'care',
        careType: 'first_aid',
        itemFilter: 'fa_shiningCapsule',
      };

    case 'evolving':
    case 'hadVisitor':
    case 'wantToInvite':
    case 'scared':
    case 'very_dirty':
    case 'very_hungry':
    case 'very_lonely':
    case 'very_pee':
    case 'very_sad':
    case 'dirty':
    case 'hungry':
    case 'lonely':
    case 'pee':
    case 'sad':
    case 'happy':
    case 'normal':
      return null;

    default:
      return assertNever(petState);
  }
}

/**
 * Resolve any pet state that prevents the player from entering a new Dream Drift run.
 *
 * @returns Whether no pet flow was needed, a pet flow completed, or entry stopped.
 */
export async function dreamDriftHandlePet(): Promise<DreamDriftPetHandlingResult> {
  // Resolve the current pet state when the signup CTA is selected.
  const state = user();
  const currentTime = now();
  const careGate = dreamDriftGetCareGate(state, currentTime);

  // Available pets require no additional Dream Drift entry handling.
  if (!careGate) return 'notNeeded';

  // Resolve the action selected for the current pet gate.
  switch (careGate.action) {
    case 'revive':
      return dreamDriftHandlePetDead();

    case 'blocked':
      return dreamDriftHandlePetBlocked(careGate.reason);

    case 'care':
      return dreamDriftHandlePetCare(careGate);

    default:
      return assertNever(careGate);
  }
}

/**
 * Resolve a dead Dream Drift pet through the existing ascension and rebirth flow.
 *
 * @returns Handled after rebirth, or blocked when entry cannot continue.
 */
async function dreamDriftHandlePetDead(): Promise<DreamDriftPetHandlingResult> {
  // Trigger the normal dead dialog that starts the pet ascension sequence.
  const pet = getPet();
  await pet.conversation.handlePetTapped();

  // Queue the event check behind the dialog-triggered ascension and rebirth.
  let hasCurrentEvent = false;
  await pet.playSequence(async () => {
    // Capture whether the event remains active after the rebirth flow.
    const refreshedState = user();
    const refreshedTime = now();
    const currentEvent = dreamDriftGetCurrentEvent(
      refreshedState,
      refreshedTime,
    );
    hasCurrentEvent = !!currentEvent;
  });

  // Stop if the event expired during the rebirth flow.
  if (!hasCurrentEvent) return 'blocked';

  // The entry sequence can now reopen the intro from the refreshed pet state.
  return 'handled';
}

/**
 * Explain a pet state that blocks Dream Drift without a care action.
 *
 * @param reason Pet state preventing Dream Drift entry.
 * @returns Blocked after presenting the existing state-specific feedback.
 */
async function dreamDriftHandlePetBlocked(
  reason: Extract<DreamDriftCareGateResult, { action: 'blocked' }>['reason'],
): Promise<DreamDriftPetHandlingResult> {
  // Select the existing feedback for every hard-blocked pet state.
  let popupReason: 'sleeping' | 'daycare';
  switch (reason) {
    case 'wantToGoOut': {
      const pet = getPet();
      await pet.conversation.waitForFinish();
      await pet.startConversation();
      return 'blocked';
    }

    case 'asleep':
      popupReason = 'sleeping';
      break;

    case 'daycare':
      popupReason = 'daycare';
      break;

    default:
      return assertNever(reason);
  }

  // Sleeping and daycare reuse the standard minigame blocker popup.
  await openPopupPromise('popupFeatureDisabled', {
    feature: 'game',
    reason: popupReason,
  });

  // The current status must be resolved before Dream Drift can continue.
  return 'blocked';
}

/**
 * Resolve a curable Dream Drift pet through the existing care flow.
 *
 * @param careGate Care type and required item selected by the shared gate.
 * @returns Handled after care without evolution, not needed when dismissed, or
 * blocked when the event expires or the pet evolves during the flow.
 */
async function dreamDriftHandlePetCare(
  careGate: Extract<DreamDriftCareGateResult, { action: 'care' }>,
): Promise<DreamDriftPetHandlingResult> {
  // Reuse the normal care picker with the status-specific guaranteed item.
  const careItemId = await openPopupPromise('popupSelectCareItem', {
    type: careGate.careType,
    itemFilter: careGate.itemFilter,
    additionalTracking: {
      itemType: careGate.careType,
    },
  });

  if (!careItemId) {
    const currentEvent = dreamDriftGetCurrentEvent(user(), now());
    return currentEvent ? 'notNeeded' : 'blocked';
  }

  // Preserve the standard insufficient-coins flow before applying the item.
  const confirmation = await puzzleCoinsConfirmCareItem(careItemId);
  if (!confirmation.accepted) {
    const currentEvent = dreamDriftGetCurrentEvent(user(), now());
    return currentEvent ? 'notNeeded' : 'blocked';
  }

  const pet = getPet();
  const wasAutoDialogBlocked = pet.conversation.isAutoDialogBlocked();

  // Prevent the standard post-care idle sequence from interrupting Dream Drift.
  pet.conversation.blockAutoDialog(true);

  try {
    // Treat the pet through the existing animated care sequence.
    await purchaseCareItemSequence(careItemId);

    // Capture whether care triggered evolution before waiting for its complete sequence.
    const didStartEvolution = pet.isEvolving();

    // Queue the final event check behind evolution and its follow-up tutorials.
    let hasCurrentEvent = false;
    await pet.playSequence(async () => {
      // Refresh event availability after every queued pet sequence finishes.
      const currentEvent = dreamDriftGetCurrentEvent(user(), now());
      hasCurrentEvent = !!currentEvent;
    });

    // Stop after evolution or event expiry so the player can re-enter from the HUD.
    if (didStartEvolution || !hasCurrentEvent) return 'blocked';

    // The entry sequence will reopen the intro from the refreshed pet state.
    return 'handled';
  } finally {
    // Restore the conversation state owned by the surrounding player flow.
    pet.conversation.blockAutoDialog(wasAutoDialogBlocked);
  }
}
